import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    "NAVSEA Certified Welding",
    "Submarine & Ship Welding",
    "Pipe Welding (CuNi)",
    "Structural Welding / Fitting",
    "MIG / TIG / Flux Core / Stick",
    "On-Site / Mobile Crews",
    "Fabrication (Machinist / Scaffold / Rigging)",
    "Other / Multiple",
];

const initialForm = {
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
};

export default function ContactPage() {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) =>
        setForm((prev) => ({ ...prev, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                company: form.company.trim() || null,
                service: form.service || null,
                message: form.message.trim(),
            };
            const res = await axios.post(`${API}/contact`, payload);
            toast.success(
                res?.data?.message ??
                    "Message received. We'll be in touch shortly.",
            );
            setForm(initialForm);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Submission failed";
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Submission failed. Please call us instead.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="contact-page" className="relative z-10 pt-32 pb-24">
            {/* HEADER */}
            <section>
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="grid grid-cols-1 md:grid-cols-12 gap-10"
                    >
                        <div className="md:col-span-7">
                            <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                                ◇ Get a Quote
                            </div>
                            <h1 className="mt-4 font-display font-black text-white text-5xl md:text-7xl uppercase tracking-tighter leading-[0.92]">
                                Tell us the scope.
                            </h1>
                            <p className="mt-6 max-w-xl text-slate-300 text-base md:text-lg leading-relaxed">
                                Share your yard, procedures, timeline, and crew
                                size. We'll respond with availability and a
                                tailored quote.
                            </p>
                        </div>
                        <div className="md:col-span-5 flex md:items-end">
                            <div className="grid grid-cols-1 gap-4 w-full">
                                <a
                                    href={`tel:${COMPANY.phoneTel}`}
                                    data-testid="contact-phone-card"
                                    className="border border-white/10 hover:border-brand-blue transition-colors p-5 flex items-center gap-4 group"
                                >
                                    <Phone className="h-5 w-5 text-brand-blue" />
                                    <div>
                                        <div className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                            Call Direct
                                        </div>
                                        <div className="font-display font-bold text-white text-lg tracking-tight">
                                            {COMPANY.phone}
                                        </div>
                                    </div>
                                </a>
                                <a
                                    href={`mailto:${COMPANY.email}`}
                                    data-testid="contact-email-card"
                                    className="border border-white/10 hover:border-brand-blue transition-colors p-5 flex items-center gap-4 group"
                                >
                                    <Mail className="h-5 w-5 text-brand-blue" />
                                    <div>
                                        <div className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                            Email
                                        </div>
                                        <div className="font-display font-bold text-white text-base tracking-tight break-all">
                                            {COMPANY.email}
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FORM */}
            <section className="mt-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            className="md:col-span-8 border border-white/10 p-6 md:p-10 bg-brand-surface/30"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Field
                                    label="Name *"
                                    testId="contact-input-name"
                                    value={form.name}
                                    onChange={update("name")}
                                    required
                                />
                                <Field
                                    label="Email *"
                                    type="email"
                                    testId="contact-input-email"
                                    value={form.email}
                                    onChange={update("email")}
                                    required
                                />
                                <Field
                                    label="Phone"
                                    type="tel"
                                    testId="contact-input-phone"
                                    value={form.phone}
                                    onChange={update("phone")}
                                />
                                <Field
                                    label="Company"
                                    testId="contact-input-company"
                                    value={form.company}
                                    onChange={update("company")}
                                />
                                <div className="md:col-span-2">
                                    <label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase block mb-2">
                                        Service Interest
                                    </label>
                                    <select
                                        data-testid="contact-input-service"
                                        value={form.service}
                                        onChange={update("service")}
                                        className="w-full bg-transparent border border-white/15 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none px-4 py-4 text-white font-body text-sm appearance-none"
                                    >
                                        <option
                                            value=""
                                            className="bg-brand-bg text-white"
                                        >
                                            Select a service…
                                        </option>
                                        {SERVICE_OPTIONS.map((s) => (
                                            <option
                                                key={s}
                                                value={s}
                                                className="bg-brand-bg text-white"
                                            >
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase block mb-2">
                                        Message *
                                    </label>
                                    <textarea
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={update("message")}
                                        required
                                        rows={6}
                                        placeholder="Yard, procedures, timeline, crew size…"
                                        className="w-full bg-transparent border border-white/15 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none px-4 py-4 text-white font-body text-sm placeholder:text-slate-500 resize-none"
                                    />
                                </div>
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                                    We typically respond within 1 business day.
                                </p>
                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={submitting}
                                    className="group inline-flex items-center justify-center gap-3 bg-brand-blue hover:bg-[#004080] disabled:opacity-60 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-8 py-4 transition-colors"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Submit Inquiry
                                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        <aside className="md:col-span-4 space-y-px bg-white/10 border border-white/10">
                            <div className="bg-brand-surface p-6">
                                <MapPin className="h-5 w-5 text-brand-blue mb-3" />
                                <div className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                    Headquarters
                                </div>
                                <div className="mt-2 text-white font-display font-bold leading-tight">
                                    {COMPANY.address.line1}
                                    <br />
                                    {COMPANY.address.line2}
                                </div>
                            </div>
                            <div className="bg-brand-surface p-6">
                                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase mb-3">
                                    Capabilities
                                </div>
                                <ul className="space-y-1.5 text-sm text-slate-300">
                                    {SERVICES.slice(0, 5).map((s) => (
                                        <li
                                            key={s.slug}
                                            className="font-display uppercase tracking-tight text-white/90"
                                        >
                                            · {s.title}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-brand-surface p-6">
                                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase mb-3">
                                    Trust
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    {[
                                        "NAVSEA",
                                        "ABS",
                                        "MIL-SPEC",
                                        "ISO Ready",
                                    ].map((c) => (
                                        <div
                                            key={c}
                                            className="border border-white/10 text-center py-2 font-display font-bold uppercase tracking-wider text-white/90"
                                        >
                                            {c}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </main>
    );
}

function Field({ label, testId, type = "text", ...props }) {
    return (
        <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase block mb-2">
                {label}
            </label>
            <input
                data-testid={testId}
                type={type}
                {...props}
                className="w-full bg-transparent border border-white/15 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none px-4 py-4 text-white font-body text-sm placeholder:text-slate-500"
            />
        </div>
    );
}
